/**
 * followUpStatus — is this lead's next follow-up overdue, due today, or ahead?
 * Card P117 PR-A (Maria, 25 Oct 2026: "clearer indicators for pending and
 * overdue follow-ups to help reps prioritize").
 *
 * ── ⛔⛔ THE SOURCE OF TRUTH, AND WHY THERE IS ONLY ONE ─────────────────────
 *
 * `crm_contacts.next_follow_up_date` (TIMESTAMPTZ, migration 040). Its own
 * migration comment says what it is: *"when the rep is supposed to reach out
 * next. Set manually or by automation. Surfaces on Daily Hit List and contact
 * list filters."* It is already selected by `crm:contacts:list` (`SELECT c.*`,
 * routes/crm.js:1087) and already offered by CrmFilterBar as both a filter and
 * a sort. Nothing new is invented here and no new column is added — this
 * module only *classifies* a value the client already holds.
 *
 * ⚠ NOT `crm_tasks.due_date`. A task is a discrete to-do that may or may not
 * exist; the follow-up date is the one field that always answers "when next".
 * Mixing them would make the badge mean two things and would need a join the
 * list does not do. If Daniel wants tasks folded in, that is a deliberate
 * second card with a decision about precedence.
 *
 * ── ⛔⛔ WHY A CALENDAR DAY AND NOT A TIMESTAMP COMPARISON ──────────────────
 *
 * The naive test is `new Date(next) < new Date()`. It is wrong in a way that
 * shows up exactly when reps are still working:
 *
 *   A follow-up set for "today" at 09:00 reads OVERDUE from 09:01, which is
 *   noise — and a follow-up due tomorrow reads OVERDUE at 23:30 local in any
 *   zone behind UTC, because the server's UTC clock has already rolled over.
 *
 * So the comparison is between CALENDAR DAY KEYS ('YYYY-MM-DD') in the
 * viewer's zone, never between instants.
 *
 * ── ⚠⚠ THIS MIRRORS THE SERVER, DELIBERATELY, AND A TEST PINS THE PAIR ─────
 *
 * `routes/_calendar.js` already does this on the server, and its helper's
 * comment states the same rule in the same words: `userTodayKey` is *"used to
 * decide whether date-only obligations (tasks/followups) are past-due relative
 * to the user's today rather than the server's UTC today."* This module uses
 * the identical `Intl.DateTimeFormat('en-CA', { timeZone })` technique.
 *
 * Two copies of a rule is how a policy number ends up decided by whichever
 * side runs first, so `tests/react/followUpBadges.test.js` drives BOTH this
 * module and the real `routes/_calendar.js` export over the same instants and
 * requires identical day keys. The duplication is guarded rather than merely
 * regretted.
 *
 * ── ⚠ WHOSE TIMEZONE — a deliberate reading, flagged in the PR ─────────────
 *
 * The card said "the clinic's timezone, not UTC". There is no single clinic
 * zone: `clinics` seeds three (America/Tijuana, America/Mexico_City,
 * America/Cancun, migration 111), a contact's clinic is an optional field, and
 * a follow-up is the REP's obligation rather than a clinic event. The server
 * already judges follow-ups against the USER's zone, so that is what this
 * matches — which for staff sitting at a clinic is the clinic's zone, and
 * which is never UTC. `tz` is an explicit parameter, so pinning this to a
 * contact's clinic later is a one-line change at the call site.
 */
"use strict";

/** States, worst first. The order IS the sort order — see compareFollowUp. */
export const FOLLOW_UP_STATES = ['overdue', 'due_today', 'upcoming', 'none'];

/**
 * Validate an IANA zone, falling back to UTC.
 * ⚠ Mirrors routes/_calendar.js `sanitizeTz`. Same rule, same fallback.
 */
export function sanitizeTz(tz) {
  if (typeof tz !== 'string') return 'UTC';
  const t = tz.trim();
  if (!t || t.length > 64 || !/^[A-Za-z0-9_+\-/]+$/.test(t)) return 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: t });
    return t;
  } catch {
    return 'UTC';
  }
}

/**
 * The calendar day ('YYYY-MM-DD') that `instant` falls on, in `tz`.
 * ⚠ Mirrors routes/_calendar.js `userTodayKey`; the pairing is tested.
 */
export function dayKeyInZone(instant, tz) {
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: sanitizeTz(tz) }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC' }).format(d);
  }
}

/** Whole days between two 'YYYY-MM-DD' keys (b - a). Calendar days, not 24h blocks. */
export function daysBetweenKeys(a, b) {
  if (!a || !b) return null;
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  // UTC midnights of two calendar days: the difference is exact whole days and
  // carries no DST, because neither operand has a local offset.
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

/**
 * Classify a contact's next follow-up.
 *
 * @param {string|Date|null} nextFollowUpDate  crm_contacts.next_follow_up_date
 * @param {object}  [opts]
 * @param {string}  [opts.tz]   IANA zone; defaults to the viewer's
 * @param {Date}    [opts.now]  injectable clock (tests pass 23:30 local)
 * @returns {{state: string, days: number|null, label: string, title: string}}
 */
export function followUpState(nextFollowUpDate, opts = {}) {
  const tz = opts.tz || viewerTz();
  const now = opts.now instanceof Date ? opts.now : new Date();

  if (nextFollowUpDate == null || nextFollowUpDate === '') {
    return {
      state: 'none', days: null, label: 'Set next action',
      title: 'No follow-up scheduled for this lead',
    };
  }

  const dueKey = dayKeyInZone(nextFollowUpDate, tz);
  const todayKey = dayKeyInZone(now, tz);
  if (!dueKey || !todayKey) {
    // An unparseable stored value is NOT silently "upcoming" — that would read
    // as a lead in good order. It reads as unscheduled, which is the honest
    // answer and the one that prompts a human to fix it.
    return {
      state: 'none', days: null, label: 'Set next action',
      title: 'This lead has no readable follow-up date',
    };
  }

  const delta = daysBetweenKeys(todayKey, dueKey);   // negative = in the past

  if (delta < 0) {
    const d = Math.abs(delta);
    return {
      state: 'overdue', days: d,
      label: d === 1 ? 'Overdue 1 day' : `Overdue ${d} days`,
      title: `Follow-up was due ${dueKey} — ${d} day${d === 1 ? '' : 's'} ago`,
    };
  }
  if (delta === 0) {
    return { state: 'due_today', days: 0, label: 'Due today', title: `Follow-up due today (${dueKey})` };
  }
  return {
    state: 'upcoming', days: delta,
    label: delta === 1 ? 'Due tomorrow' : `Due in ${delta} days`,
    title: `Follow-up due ${dueKey}`,
  };
}

/** The viewer's IANA zone — the pattern already used at OpportunitiesTab.js:1607. */
export function viewerTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
  catch { return 'UTC'; }
}

/**
 * Sort comparator: worst first, and WITHIN overdue the longest-overdue first.
 *
 * ⚠ The tie-break matters. Maria's ask is prioritisation, and "overdue" alone
 * is not a priority — eleven days late outranks one. Leads with no follow-up
 * set sort LAST: they are a gap to fill, not an emergency, and putting them on
 * top would bury the genuinely late ones behind the never-scheduled ones.
 */
export function compareFollowUp(a, b) {
  const ra = FOLLOW_UP_STATES.indexOf(a.state);
  const rb = FOLLOW_UP_STATES.indexOf(b.state);
  if (ra !== rb) return ra - rb;
  if (a.state === 'overdue') return (b.days || 0) - (a.days || 0);   // most overdue first
  if (a.state === 'upcoming') return (a.days || 0) - (b.days || 0);  // soonest first
  return 0;
}
